import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, TrendingUp, TrendingDown, DollarSign, Percent, PieChart, BarChart3, Activity, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Target, ShieldAlert, AlertTriangle, Cloud, Info, Download, CalendarIcon, X, Clock } from "lucide-react";
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { SavedDataMenu } from '@/components/SavedDataMenu';
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PieChart as RechartsPieChart, BarChart, XAxis, YAxis, CartesianGrid, Cell, Pie, Tooltip, Legend, ResponsiveContainer, Bar, LineChart, Line } from "recharts";
import { useNavigate, useLocation } from 'react-router-dom';
import { IndividualStockScorecard } from '@/components/IndividualStockScorecard';
import { supabase } from '@/integrations/supabase/client';
import { cn } from "@/lib/utils";
import { IchimokuAlerts } from '@/components/IchimokuAlerts';
import { parseIBKRCSV, getFormatDescription, type ParseResult, type FinancialSummary } from '@/lib/ibkrParsers';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, isWithinInterval, parseISO, isValid } from "date-fns";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { useImportPersistence, formatImportTimestamp } from "@/hooks/use-import-persistence";
import { LastImportBadge } from "@/components/LastImportBadge";
import { LoadSavedDataDialog } from "@/components/LoadSavedDataDialog";
import { LoadSavedDataButton } from "@/components/LoadSavedDataButton";

interface AccountSummary {
  account: string;
  name: string;
  beginningNAV: number;
  endingNAV: number;
  return: number;
  deposits: number;
  withdrawals: number;
  dividends: number;
  interest: number;
  fees: number;
}

interface KeyStatistics {
  beginningNAV: number;
  endingNAV: number;
  cumulativeReturn: number;
  fiveDayReturn: number;
  tenDayReturn: number;
  bestReturn: number;
  bestReturnDate: string;
  worstReturn: number;
  worstReturnDate: string;
  mtm: number;
  depositsWithdrawals: number;
  dividends: number;
  interest: number;
  feesCommissions: number;
  changeInNAV: number;
}

interface Position {
  ticker: string;
  description: string;
  sector: string;
  quantity: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currentPrice: number;
  avgCost: number;
  currency: string;
}

interface BenchmarkComparison {
  period: string;
  portfolio: number | null;
  sp500: number | null;
  efa: number | null;
  vt: number | null;
}

interface BenchmarkSummary {
  name: string;
  mtd: number | null;
  qtd: number | null;
  ytd: number | null;
  oneYear: number | null;
  sinceInception: number | null;
}

interface PositionsByCategory {
  category: string;
  positions: Position[];
  totalValue: number;
  totalCostBasis: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPercent: number;
}

interface AnalysisData {
  accountSummary: AccountSummary[];
  keyStatistics: KeyStatistics;
  positionsByCategory: PositionsByCategory[];
  allPositions: Position[];
  monthlyComparison: BenchmarkComparison[];
  quarterlyComparison: BenchmarkComparison[];
  benchmarkSummary: BenchmarkSummary[];
  totalValue: number;
  totalCostBasis: number;
  totalUnrealizedPL: number;
  totalUnrealizedPLPercent: number;
}

type SortField = keyof Position;
type SortDirection = 'asc' | 'desc' | null;

interface IBKRImportData {
  id: string;
  user_id: string;
  account_id: string | null;
  statement_period: string | null;
  currency: string | null;
  cash_balance: number | null;
  positions_data: any;
  trades_data: any;
  dividends_data: any;
  cash_transactions_data: any;
  cash_balances_data: any;
  financial_summary_data: any;
  imported_at: string;
  updated_at: string;
}

const IBKRImport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [sortField, setSortField] = useState<SortField>('marketValue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [detectedFormat, setDetectedFormat] = useState<ParseResult['format'] | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  
  // Date range filters for each tab
  const [tradesDateFrom, setTradesDateFrom] = useState<Date | undefined>(undefined);
  const [tradesDateTo, setTradesDateTo] = useState<Date | undefined>(undefined);
  const [dividendsDateFrom, setDividendsDateFrom] = useState<Date | undefined>(undefined);
  const [dividendsDateTo, setDividendsDateTo] = useState<Date | undefined>(undefined);
  const [cashDateFrom, setCashDateFrom] = useState<Date | undefined>(undefined);
  const [cashDateTo, setCashDateTo] = useState<Date | undefined>(undefined);
  
  const { toast } = useToast();
  
  // Import persistence hook
  const { lastImport, savedData, loading: loadingSaved, saveImport } = useImportPersistence<IBKRImportData>('ibkr_imports');
  
  // State for saved data dialog
  const [showSavedDataDialog, setShowSavedDataDialog] = useState(false);
  const [hasCheckedSavedData, setHasCheckedSavedData] = useState(false);

  // Check for autoLoad from navigation state (from Collected Data page)
  useEffect(() => {
    const state = location.state as { autoLoad?: boolean } | null;
    if (state?.autoLoad && savedData && !loadingSaved) {
      handleLoadSavedData();
      // Clear the state to prevent re-loading on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, savedData, loadingSaved]);

  // Check for saved data on mount and show dialog
  useEffect(() => {
    const state = location.state as { autoLoad?: boolean } | null;
    if (!loadingSaved && savedData && !hasCheckedSavedData && !parseResult && !state?.autoLoad) {
      setShowSavedDataDialog(true);
      setHasCheckedSavedData(true);
    } else if (!loadingSaved && !savedData) {
      setHasCheckedSavedData(true);
    }
  }, [loadingSaved, savedData, hasCheckedSavedData, parseResult, location.state]);

  const handleLoadSavedData = () => {
    if (savedData) {
      const restored: ParseResult = {
        format: 'activity_statement',
        positions: savedData.positions_data || [],
        trades: savedData.trades_data || [],
        dividends: savedData.dividends_data || [],
        cashTransactions: savedData.cash_transactions_data || [],
        cashBalances: savedData.cash_balances_data || [],
        financialSummary: savedData.financial_summary_data || {
          deposits: 0,
          withdrawals: 0,
          dividends: 0,
          interest: 0,
          realizedPL: 0,
          feesAndCommissions: 0
        },
        accountId: savedData.account_id || undefined,
        statementPeriod: savedData.statement_period || undefined,
        currency: savedData.currency || 'USD',
        cashBalance: savedData.cash_balance || undefined
      };
      setParseResult(restored);
      setDetectedFormat('activity_statement');
      toast({
        title: "Data Loaded",
        description: "Your saved IBKR analysis has been restored.",
      });
    }
    setShowSavedDataDialog(false);
  };

  const handleDeclineSavedData = () => {
    setShowSavedDataDialog(false);
  };


  // Helper function to parse date strings from IBKR format
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Try various formats: YYYY-MM-DD, MM/DD/YYYY, DD-MMM-YY, etc.
    const cleanDate = dateStr.split(',')[0].split(' ')[0].trim();
    
    // Try ISO format first
    let parsed = parseISO(cleanDate);
    if (isValid(parsed)) return parsed;
    
    // Try MM/DD/YYYY format
    const slashParts = cleanDate.split('/');
    if (slashParts.length === 3) {
      const [month, day, year] = slashParts;
      parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isValid(parsed)) return parsed;
    }
    
    // Try DD-MMM-YY format
    const dashParts = cleanDate.split('-');
    if (dashParts.length === 3) {
      const months: Record<string, number> = {
        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
      };
      const day = parseInt(dashParts[0]);
      const month = months[dashParts[1].toUpperCase()];
      let year = parseInt(dashParts[2]);
      if (year < 100) year += 2000;
      if (!isNaN(day) && month !== undefined && !isNaN(year)) {
        parsed = new Date(year, month, day);
        if (isValid(parsed)) return parsed;
      }
    }
    
    return null;
  };

  // Filter function for date range
  const isInDateRange = (dateStr: string, fromDate?: Date, toDate?: Date): boolean => {
    if (!fromDate && !toDate) return true;
    
    const date = parseDateString(dateStr);
    if (!date) return true; // Include items with unparseable dates
    
    if (fromDate && toDate) {
      return isWithinInterval(date, { start: fromDate, end: toDate });
    }
    if (fromDate) {
      return date >= fromDate;
    }
    if (toDate) {
      return date <= toDate;
    }
    return true;
  };

  // Filtered data memos
  const filteredTrades = useMemo(() => {
    if (!parseResult?.trades) return [];
    return parseResult.trades.filter(trade => isInDateRange(trade.date, tradesDateFrom, tradesDateTo));
  }, [parseResult?.trades, tradesDateFrom, tradesDateTo]);

  const filteredDividends = useMemo(() => {
    if (!parseResult?.dividends) return [];
    return parseResult.dividends.filter(div => isInDateRange(div.date, dividendsDateFrom, dividendsDateTo));
  }, [parseResult?.dividends, dividendsDateFrom, dividendsDateTo]);

  const filteredCashTransactions = useMemo(() => {
    if (!parseResult?.cashTransactions) return [];
    return parseResult.cashTransactions.filter(txn => isInDateRange(txn.date, cashDateFrom, cashDateTo));
  }, [parseResult?.cashTransactions, cashDateFrom, cashDateTo]);


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortField('marketValue');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedPositions = (positions: Position[]) => {
    if (!sortDirection) {
      return [...positions].sort((a, b) => b.marketValue - a.marketValue);
    }

    return [...positions].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
    if (sortDirection === 'asc') return <ArrowUp className="w-4 h-4 ml-1 inline" />;
    if (sortDirection === 'desc') return <ArrowDown className="w-4 h-4 ml-1 inline" />;
    return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
  };

  const parseCSV = (csvText: string): AnalysisData => {
    const lines = csvText.split('\n');
    const accountSummary: AccountSummary[] = [];
    const positions: Position[] = [];
    const positionsMap = new Map<string, Position[]>();
    const monthlyData: BenchmarkComparison[] = [];
    const quarterlyData: BenchmarkComparison[] = [];
    const benchmarkSummaryData: BenchmarkSummary[] = [];
    let keyStats: KeyStatistics | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parts = line.split(',');
      
      if (parts[0] === 'Breakdown of Accounts' && parts[1] === 'Data') {
        accountSummary.push({
          account: parts[2],
          name: parts[3],
          beginningNAV: parseFloat(parts[4]) || 0,
          endingNAV: parseFloat(parts[5]) || 0,
          return: parseFloat(parts[6]) || 0,
          deposits: parseFloat(parts[7]) || 0,
          withdrawals: parseFloat(parts[8]) || 0,
          dividends: parseFloat(parts[9]) || 0,
          interest: parseFloat(parts[10]) || 0,
          fees: parseFloat(parts[11]) || 0,
        });
      }

      if (parts[0] === 'Key Statistics' && parts[1] === 'Data') {
        keyStats = {
          beginningNAV: parseFloat(parts[2]) || 0,
          endingNAV: parseFloat(parts[3]) || 0,
          cumulativeReturn: parseFloat(parts[4]) || 0,
          fiveDayReturn: parseFloat(parts[5]) || 0,
          tenDayReturn: parseFloat(parts[7]) || 0,
          bestReturn: parseFloat(parts[9]) || 0,
          bestReturnDate: parts[10] || '',
          worstReturn: parseFloat(parts[11]) || 0,
          worstReturnDate: parts[12] || '',
          mtm: parseFloat(parts[13]) || 0,
          depositsWithdrawals: parseFloat(parts[14]) || 0,
          dividends: parseFloat(parts[15]) || 0,
          interest: parseFloat(parts[16]) || 0,
          feesCommissions: parseFloat(parts[17]) || 0,
          changeInNAV: parseFloat(parts[19]) || 0,
        };
      }

      if (parts[0] === 'Open Position Summary' && parts[1] === 'Data' && parts[3] !== 'Total') {
        const category = parts[3];
        const currency = parts[4]?.trim() || 'USD'; // Currency is in column 4
        const ticker = parts[5] || '';
        const description = parts[6] || '';
        const quantity = parseFloat(parts[8]) || 0;
        const marketValue = parseFloat(parts[10]) || 0;
        const costBasis = parseFloat(parts[11]) || 0;
        const unrealizedPL = parseFloat(parts[12]) || 0;

        if (ticker && category !== 'Cash') {
          const position: Position = {
            ticker,
            description,
            sector: parts[7] || '',
            quantity,
            marketValue,
            costBasis,
            unrealizedPL,
            unrealizedPLPercent: costBasis !== 0 ? (unrealizedPL / Math.abs(costBasis)) * 100 : 0,
            currentPrice: quantity !== 0 ? marketValue / quantity : 0,
            avgCost: quantity !== 0 ? costBasis / quantity : 0,
            currency,
          };

          if (!positionsMap.has(category)) {
            positionsMap.set(category, []);
          }
          positionsMap.get(category)!.push(position);
          positions.push(position);
        }
      }

      // Parse benchmark summary (MTD, QTD, YTD, etc.)
      if (parts[0] === 'Historical Performance Benchmark Comparison' && parts[1] === 'Data') {
        const period = parts[2];
        
        // Benchmark summary rows
        if (period && !period.match(/^\d{4}/) && !period.includes('Q') && parts[3]) {
          const benchmarkName = period === 'Consolidated' ? 'Portfolio' : period;
          benchmarkSummaryData.push({
            name: benchmarkName,
            mtd: parts[3] ? parseFloat(parts[3]) : null,
            qtd: parts[4] ? parseFloat(parts[4]) : null,
            ytd: parts[5] ? parseFloat(parts[5]) : null,
            oneYear: parts[6] ? parseFloat(parts[6]) : null,
            sinceInception: parts[7] ? parseFloat(parts[7]) : null,
          });
        }
        
        // Monthly performance (both 2024 and 2025)
        if (period && period.match(/^\d{6}$/) && parts[10]) {
          monthlyData.push({
            period,
            portfolio: parts[10] ? parseFloat(parts[10]) : null,
            sp500: parts[4] ? parseFloat(parts[4]) : null,
            efa: parts[6] ? parseFloat(parts[6]) : null,
            vt: parts[8] ? parseFloat(parts[8]) : null,
          });
        }
        
        // Quarterly performance
        if (period && period.includes('Q') && parts[10]) {
          quarterlyData.push({
            period,
            portfolio: parts[10] ? parseFloat(parts[10]) : null,
            sp500: parts[4] ? parseFloat(parts[4]) : null,
            efa: parts[6] ? parseFloat(parts[6]) : null,
            vt: parts[8] ? parseFloat(parts[8]) : null,
          });
        }
      }
    }

    const positionsByCategory: PositionsByCategory[] = Array.from(positionsMap.entries()).map(([category, categoryPositions]) => {
      const totalValue = categoryPositions.reduce((sum, p) => sum + p.marketValue, 0);
      const totalCostBasis = categoryPositions.reduce((sum, p) => sum + p.costBasis, 0);
      const totalUnrealizedPL = categoryPositions.reduce((sum, p) => sum + p.unrealizedPL, 0);
      
      return {
        category,
        positions: categoryPositions,
        totalValue,
        totalCostBasis,
        totalUnrealizedPL,
        totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
      };
    });

    const totalValue = positions.reduce((sum, p) => sum + p.marketValue, 0);
    const totalCostBasis = positions.reduce((sum, p) => sum + p.costBasis, 0);
    const totalUnrealizedPL = positions.reduce((sum, p) => sum + p.unrealizedPL, 0);

    return {
      accountSummary,
      keyStatistics: keyStats || {
        beginningNAV: 0,
        endingNAV: 0,
        cumulativeReturn: 0,
        fiveDayReturn: 0,
        tenDayReturn: 0,
        bestReturn: 0,
        bestReturnDate: '',
        worstReturn: 0,
        worstReturnDate: '',
        mtm: 0,
        depositsWithdrawals: 0,
        dividends: 0,
        interest: 0,
        feesCommissions: 0,
        changeInNAV: 0,
      },
      positionsByCategory,
      allPositions: positions,
      monthlyComparison: monthlyData,
      quarterlyComparison: quarterlyData,
      benchmarkSummary: benchmarkSummaryData,
      totalValue,
      totalCostBasis,
      totalUnrealizedPL,
      totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
    };
  };

  const handleFileUpload = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const text = await file.text();
      
      // Use the new multi-format parser
      const ibkrResult = parseIBKRCSV(text);
      setDetectedFormat(ibkrResult.format);
      setParseResult(ibkrResult);
      
      // Convert parsed positions to internal format
      const convertedPositions: Position[] = ibkrResult.positions.map(pos => ({
        ticker: pos.ticker,
        description: pos.description,
        sector: pos.sector,
        quantity: pos.quantity,
        marketValue: pos.marketValue,
        costBasis: pos.costBasis,
        unrealizedPL: pos.unrealizedPL,
        unrealizedPLPercent: pos.unrealizedPLPercent,
        currentPrice: pos.currentPrice,
        avgCost: pos.avgCost,
        currency: pos.currency,
      }));
      
      // Also use the legacy parser for additional data (benchmarks, key stats, etc.)
      const legacyData = parseCSV(text);
      
      // Merge: use new parser positions if we got more, otherwise fall back to legacy
      const finalPositions = convertedPositions.length > 0 ? convertedPositions : legacyData.allPositions;
      
      // Rebuild categories from positions
      const positionsMap = new Map<string, Position[]>();
      for (const pos of finalPositions) {
        const category = pos.sector || 'Stocks';
        if (!positionsMap.has(category)) {
          positionsMap.set(category, []);
        }
        positionsMap.get(category)!.push(pos);
      }
      
      const positionsByCategory = Array.from(positionsMap.entries()).map(([category, categoryPositions]) => {
        const totalValue = categoryPositions.reduce((sum, p) => sum + p.marketValue, 0);
        const totalCostBasis = categoryPositions.reduce((sum, p) => sum + p.costBasis, 0);
        const totalUnrealizedPL = categoryPositions.reduce((sum, p) => sum + p.unrealizedPL, 0);
        
        return {
          category,
          positions: categoryPositions,
          totalValue,
          totalCostBasis,
          totalUnrealizedPL,
          totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
        };
      });
      
      const totalValue = finalPositions.reduce((sum, p) => sum + p.marketValue, 0);
      const totalCostBasis = finalPositions.reduce((sum, p) => sum + p.costBasis, 0);
      const totalUnrealizedPL = finalPositions.reduce((sum, p) => sum + p.unrealizedPL, 0);
      
      const data: AnalysisData = {
        ...legacyData,
        allPositions: finalPositions,
        positionsByCategory,
        totalValue,
        totalCostBasis,
        totalUnrealizedPL,
        totalUnrealizedPLPercent: totalCostBasis !== 0 ? (totalUnrealizedPL / Math.abs(totalCostBasis)) * 100 : 0,
      };
      
      setAnalysisData(data);
      
      // Save positions to database
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const positionsToSave = finalPositions.map(pos => ({
          user_id: user.id,
          ticker: pos.ticker,
          stock_name: pos.description,
          buy_price: pos.avgCost,
          quantity: pos.quantity,
          price_per_share: pos.currentPrice,
          total_value: pos.marketValue,
          local_currency: pos.currency,
          gain_pct: pos.unrealizedPLPercent / 100,
          added_value_chf: pos.unrealizedPL,
          initial_investment: pos.costBasis
        }));

        const { error: insertError } = await supabase
          .from('positions')
          .insert(positionsToSave);

        if (insertError) {
          console.error('Error saving positions:', insertError);
        } else {
          // Send portfolio report email
          supabase.functions.invoke('send-portfolio-report', {
            body: {
              positions: finalPositions.map(pos => ({
                ticker: pos.ticker,
                stock_name: pos.description,
                quantity: pos.quantity,
                buy_price: pos.avgCost,
                price_per_share: pos.currentPrice,
                total_value: pos.marketValue,
                gain_pct: pos.unrealizedPLPercent,
                added_value_chf: pos.unrealizedPL,
                local_currency: pos.currency
              })),
              userEmail: user.email,
              importType: 'IBKR'
            }
          }).then(response => {
            if (response.error) {
              console.error('Failed to send portfolio report:', response.error);
            } else {
              console.log('Portfolio report sent successfully');
            }
          });
        }
        
        // Save the import data for persistence
        const saved = await saveImport({
          account_id: ibkrResult.accountId || null,
          statement_period: ibkrResult.statementPeriod || null,
          currency: ibkrResult.currency || 'USD',
          cash_balance: ibkrResult.cashBalance || null,
          positions_data: ibkrResult.positions,
          trades_data: ibkrResult.trades,
          dividends_data: ibkrResult.dividends,
          cash_transactions_data: ibkrResult.cashTransactions,
          cash_balances_data: ibkrResult.cashBalances,
          financial_summary_data: ibkrResult.financialSummary
        });

        if (!saved) {
          toast({
            title: "Auto-save failed",
            description: "Your CSV was parsed, but saving it to the database failed. Please try again (and ensure you are logged in).",
            variant: "destructive",
          });
        }
      }
      
      const formatName = getFormatDescription(ibkrResult.format);
      toast({
        title: "Success",
        description: `Detected format: ${formatName}. Parsed ${finalPositions.length} positions${ibkrResult.trades.length > 0 ? ` and ${ibkrResult.trades.length} trades` : ''}.`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to process CSV file",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D', '#8DD1E1', '#D084D0'];
  
  const getCurrencySymbol = (currency: string | undefined): string => {
    if (!currency || typeof currency !== 'string') return '?';
    
    const symbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'CHF': 'CHF',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'HKD': 'HK$',
      'SGD': 'S$',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
    };
    
    const upperCurrency = currency.toUpperCase().trim();
    return symbols[upperCurrency] || upperCurrency;
  };
  
  const formatCurrency = (value: number, currency: string = 'USD') => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  // Export dividends to CSV for tax reporting
  const exportDividendsToCSV = () => {
    if (!parseResult?.dividends || parseResult.dividends.length === 0) {
      toast({
        title: "No data to export",
        description: "No dividend data available to export",
        variant: "destructive",
      });
      return;
    }

    // Calculate summary data
    const grossDividends = parseResult.dividends
      .filter(d => d.amount > 0 && !d.description.toLowerCase().includes('tax'))
      .reduce((sum, d) => sum + d.amount, 0);
    
    const withholdingTax = Math.abs(parseResult.dividends
      .filter(d => d.amount < 0 || d.description.toLowerCase().includes('tax'))
      .reduce((sum, d) => sum + d.amount, 0));
    
    const netDividends = parseResult.dividends.reduce((sum, d) => sum + d.amount, 0);

    // Group by ticker for summary
    const dividendsByTicker = parseResult.dividends.reduce((acc, div) => {
      if (!div.ticker) return acc;
      if (!acc[div.ticker]) {
        acc[div.ticker] = { gross: 0, tax: 0, net: 0, currency: div.currency };
      }
      if (div.description.toLowerCase().includes('tax') || div.amount < 0) {
        acc[div.ticker].tax += Math.abs(div.amount);
      } else {
        acc[div.ticker].gross += div.amount;
      }
      acc[div.ticker].net = acc[div.ticker].gross - acc[div.ticker].tax;
      return acc;
    }, {} as Record<string, { gross: number; tax: number; net: number; currency: string }>);

    // Build CSV content
    const lines: string[] = [];
    
    // Header info
    lines.push('DIVIDEND TAX REPORT');
    lines.push(`Generated: ${new Date().toISOString().split('T')[0]}`);
    if (parseResult.accountId) lines.push(`Account: ${parseResult.accountId}`);
    if (parseResult.statementPeriod) lines.push(`Statement Period: ${parseResult.statementPeriod}`);
    lines.push('');
    
    // Summary section
    lines.push('SUMMARY');
    lines.push(`Gross Dividends,${grossDividends.toFixed(2)},${parseResult.currency || 'USD'}`);
    lines.push(`Withholding Tax,${withholdingTax.toFixed(2)},${parseResult.currency || 'USD'}`);
    lines.push(`Net Dividends,${netDividends.toFixed(2)},${parseResult.currency || 'USD'}`);
    lines.push('');
    
    // By ticker summary
    lines.push('DIVIDENDS BY STOCK');
    lines.push('Ticker,Gross Dividend,Withholding Tax,Net Dividend,Currency');
    Object.entries(dividendsByTicker)
      .sort((a, b) => b[1].net - a[1].net)
      .forEach(([ticker, data]) => {
        lines.push(`${ticker},${data.gross.toFixed(2)},${data.tax.toFixed(2)},${data.net.toFixed(2)},${data.currency}`);
      });
    lines.push('');
    
    // Detailed transactions
    lines.push('DETAILED TRANSACTIONS');
    lines.push('Date,Type,Ticker,Description,Amount,Currency');
    parseResult.dividends.forEach(div => {
      const isTax = div.description.toLowerCase().includes('tax') || div.amount < 0;
      const type = isTax ? 'Withholding Tax' : 'Dividend';
      // Escape description for CSV (handle commas and quotes)
      const escapedDesc = `"${div.description.replace(/"/g, '""')}"`;
      lines.push(`${div.date},${type},${div.ticker || ''},${escapedDesc},${div.amount.toFixed(2)},${div.currency}`);
    });

    // Create and download CSV
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dividend_tax_report_${parseResult.accountId || 'IBKR'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "Dividend tax report has been downloaded",
    });
  };

  return (
    <>
      <LoadSavedDataDialog
        open={showSavedDataDialog}
        onOpenChange={setShowSavedDataDialog}
        onConfirm={handleLoadSavedData}
        onDecline={handleDeclineSavedData}
        importedAt={lastImport.importedAt}
        dataType="IBKR Analysis"
      />
      
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background p-4 md:p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="hover:bg-muted"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-600 to-pink-600">
                IBKR Full Analysis PF
              </h1>
              <p className="text-sm md:text-lg text-muted-foreground mt-2">
                Upload your Interactive Brokers CSV statement for comprehensive portfolio analysis
              </p>
              {lastImport.importedAt && (
                <div className="mt-3">
                  <LastImportBadge importedAt={lastImport.importedAt} label="Last analysis" />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SavedDataMenu />
            <AutoSaveIndicator />
          </div>
        </div>

        {/* Trading Guidelines Section */}
        <Card className="border-primary/20 bg-gradient-to-br from-background to-muted/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Target className="h-5 w-5 text-primary" />
              Swing Trading Guidelines
            </CardTitle>
            <CardDescription>
              Professional strategies for profit-taking and risk management
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="profit-taking">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📈</span>
                    <span className="font-semibold">Profit-Taking Alerts (Added Value Gain)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-sm space-y-4 pt-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-primary">Reward-to-Risk Ratio:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Many swing traders aim for at least <strong className="text-foreground">3:1 reward:risk</strong></li>
                      <li><strong className="text-foreground">Example:</strong> If risking 5% (stop-loss), set profit target at ~15% above entry</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-primary">Technical Levels:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Place profit alerts near resistance zones or prior swing highs</li>
                      <li>If the stock has strong momentum, targets can extend to 4:1 or 6:1 reward:risk</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-primary">Partial Profit-Taking:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Sell part of the position at the first target (e.g., 2:1) and let the rest run toward higher levels</li>
                      <li>This locks in gains while keeping upside potential</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="loss-protection">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📉</span>
                    <span className="font-semibold">Loss Protection Alerts (Stop-Loss)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-sm space-y-4 pt-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-destructive">Percentage-Based Stops:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Commonly <strong className="text-foreground">5–10%</strong> below entry price for swing trades</li>
                      <li><strong className="text-foreground">Example:</strong> Buy at $100, stop-loss at $95 (5% risk)</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-destructive">Technical Stops:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Place stop-loss just below the most recent swing low in an uptrend</li>
                      <li>Or above the swing high in a short trade</li>
                      <li>This aligns with natural support/resistance levels</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-destructive">Dynamic Stops with Moving Averages:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
                      <li>Use the 20-day or 50-day moving average as a trailing stop</li>
                      <li>If price closes below the moving average, exit the trade</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {!analysisData && !parseResult && (
          <Card className="border-2 border-dashed border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload CSV Statement
              </CardTitle>
              <CardDescription>
                Supports multiple IBKR formats: Portfolio Analyst, Activity Statement, Trade Confirmations, and Flex Queries
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Load Saved Data Button */}
              {lastImport.importedAt && (
                <LoadSavedDataButton
                  importedAt={lastImport.importedAt}
                  onLoad={handleLoadSavedData}
                  loading={loadingSaved}
                  label="Load Last Analysis"
                />
              )}
              {isProcessing ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center p-8">
                    <div className="text-center space-y-4">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
                      <p className="text-muted-foreground">Processing CSV file...</p>
                    </div>
                  </div>
                  <Progress value={66} className="w-full" />
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) handleFileUpload(file);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Drop your CSV file here</p>
                  <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Select CSV File
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {analysisData && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Portfolio Analysis</h2>
                <Button onClick={() => { setAnalysisData(null); setDetectedFormat(null); setParseResult(null); }} variant="outline">
                  Upload New File
                </Button>
              </div>
              
              {/* Format Detection Info - Enhanced */}
              {detectedFormat && (
                <Alert className="border-primary/20 bg-primary/5">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Format Detected: {getFormatDescription(detectedFormat)}</AlertTitle>
                  <AlertDescription className="text-muted-foreground">
                    <div className="flex flex-wrap gap-4 mt-2">
                      <span className="inline-flex items-center gap-1">
                        <Badge variant="secondary">{parseResult?.positions.length || 0}</Badge> positions
                      </span>
                      {parseResult?.trades && parseResult.trades.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="secondary">{parseResult.trades.length}</Badge> trades
                        </span>
                      )}
                      {parseResult?.dividends && parseResult.dividends.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="secondary">{parseResult.dividends.length}</Badge> dividends
                        </span>
                      )}
                      {parseResult?.cashTransactions && parseResult.cashTransactions.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="secondary">{parseResult.cashTransactions.length}</Badge> cash transactions
                        </span>
                      )}
                      {parseResult?.accountId && (
                        <span className="text-sm">Account: <strong>{parseResult.accountId}</strong></span>
                      )}
                      {parseResult?.statementPeriod && (
                        <span className="text-sm">Period: <strong>{parseResult.statementPeriod}</strong></span>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Key Statistics Cards - Enhanced */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Portfolio Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatCurrency(analysisData.totalValue, 'USD')}</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {analysisData.keyStatistics.endingNAV > 0 
                      ? `NAV: ${formatCurrency(analysisData.keyStatistics.endingNAV, 'USD')}`
                      : `${analysisData.allPositions.length} positions`}
                  </p>
                </CardContent>
              </Card>

              <Card className={analysisData.totalUnrealizedPL >= 0 ? "bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20" : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Unrealized P/L</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${analysisData.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(analysisData.totalUnrealizedPL, 'USD')}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {analysisData.totalUnrealizedPL >= 0 ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
                    <span className={`text-sm font-medium ${analysisData.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.totalUnrealizedPLPercent)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className={analysisData.keyStatistics.cumulativeReturn >= 0 ? "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20" : "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {analysisData.keyStatistics.cumulativeReturn !== 0 ? 'YTD Return' : 'Total Cost Basis'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analysisData.keyStatistics.cumulativeReturn !== 0 ? (
                    <>
                      <div className={`text-3xl font-bold ${analysisData.keyStatistics.cumulativeReturn >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        {formatPercent(analysisData.keyStatistics.cumulativeReturn)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">Change: {formatCurrency(analysisData.keyStatistics.changeInNAV, 'USD')}</p>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold">{formatCurrency(analysisData.totalCostBasis, 'USD')}</div>
                      <p className="text-sm text-muted-foreground mt-1">Total invested</p>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {parseResult?.cashBalance ? 'Cash Balance' : 'Cost Basis'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {parseResult?.cashBalance 
                      ? formatCurrency(parseResult.cashBalance, parseResult.currency || 'USD')
                      : formatCurrency(analysisData.totalCostBasis, 'USD')}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {parseResult?.cashBalance 
                      ? `Available cash`
                      : `${analysisData.allPositions.length} positions`}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Financial Summary from Activity Statement */}
            {parseResult?.financialSummary && (parseResult.financialSummary.dividendsReceived !== 0 || parseResult.financialSummary.realizedPL !== 0 || parseResult.trades.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Financial Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {parseResult.financialSummary.dividendsReceived !== 0 && (
                      <div className="space-y-1 p-3 bg-green-500/10 rounded-lg">
                        <p className="text-sm text-muted-foreground">Dividends</p>
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(parseResult.financialSummary.dividendsReceived, parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                    {parseResult.financialSummary.interestReceived !== 0 && (
                      <div className="space-y-1 p-3 bg-blue-500/10 rounded-lg">
                        <p className="text-sm text-muted-foreground">Interest</p>
                        <p className="text-lg font-bold text-blue-600">
                          {formatCurrency(parseResult.financialSummary.interestReceived, parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                    {parseResult.financialSummary.realizedPL !== 0 && (
                      <div className={`space-y-1 p-3 rounded-lg ${parseResult.financialSummary.realizedPL >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        <p className="text-sm text-muted-foreground">Realized P/L</p>
                        <p className={`text-lg font-bold ${parseResult.financialSummary.realizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(parseResult.financialSummary.realizedPL, parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                    {parseResult.financialSummary.feesAndCommissions !== 0 && (
                      <div className="space-y-1 p-3 bg-red-500/10 rounded-lg">
                        <p className="text-sm text-muted-foreground">Fees & Commissions</p>
                        <p className="text-lg font-bold text-red-600">
                          -{formatCurrency(Math.abs(parseResult.financialSummary.feesAndCommissions), parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                    {parseResult.financialSummary.deposits !== 0 && (
                      <div className="space-y-1 p-3 bg-purple-500/10 rounded-lg">
                        <p className="text-sm text-muted-foreground">Deposits</p>
                        <p className="text-lg font-bold text-purple-600">
                          {formatCurrency(parseResult.financialSummary.deposits, parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                    {parseResult.financialSummary.withdrawals !== 0 && (
                      <div className="space-y-1 p-3 bg-orange-500/10 rounded-lg">
                        <p className="text-sm text-muted-foreground">Withdrawals</p>
                        <p className="text-lg font-bold text-orange-600">
                          -{formatCurrency(parseResult.financialSummary.withdrawals, parseResult.currency || 'USD')}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Trades Summary */}
                  {parseResult.trades.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-green-500/20 text-green-700">
                            {parseResult.trades.filter(t => t.action === 'BUY').length} Buys
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-red-500/20 text-red-700">
                            {parseResult.trades.filter(t => t.action === 'SELL').length} Sells
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Total volume: {formatCurrency(
                            parseResult.trades.reduce((sum, t) => sum + t.proceeds, 0),
                            parseResult.currency || 'USD'
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Cash Balances by Currency */}
                  {parseResult.cashBalances && parseResult.cashBalances.length > 1 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Cash by Currency:</p>
                      <div className="flex flex-wrap gap-2">
                        {parseResult.cashBalances.map((cb, idx) => (
                          <Badge key={idx} variant="outline" className="text-sm">
                            {cb.currency}: {formatCurrency(cb.balance, cb.currency)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}


            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">5-Day Return</p>
                    <p className={`text-lg font-bold ${analysisData.keyStatistics.fiveDayReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.keyStatistics.fiveDayReturn)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">10-Day Return</p>
                    <p className={`text-lg font-bold ${analysisData.keyStatistics.tenDayReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(analysisData.keyStatistics.tenDayReturn)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Best Day</p>
                    <p className="text-lg font-bold text-green-600">{formatPercent(analysisData.keyStatistics.bestReturn)}</p>
                    <p className="text-xs text-muted-foreground">{analysisData.keyStatistics.bestReturnDate}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Worst Day</p>
                    <p className="text-lg font-bold text-red-600">{formatPercent(analysisData.keyStatistics.worstReturn)}</p>
                    <p className="text-xs text-muted-foreground">{analysisData.keyStatistics.worstReturnDate}</p>
                  </div>
                </div>

                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Dividends</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.dividends, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Interest</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.interest, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Fees</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(analysisData.keyStatistics.feesCommissions, 'USD')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Net Deposits</p>
                    <p className="text-lg font-bold">{formatCurrency(analysisData.keyStatistics.depositsWithdrawals, 'USD')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Summary - Show if multiple accounts */}
            {analysisData.accountSummary.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Account Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Account</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-right p-2">Beginning NAV</th>
                          <th className="text-right p-2">Ending NAV</th>
                          <th className="text-right p-2">Return %</th>
                          <th className="text-right p-2">Deposits</th>
                          <th className="text-right p-2">Withdrawals</th>
                          <th className="text-right p-2">Dividends</th>
                          <th className="text-right p-2">Interest</th>
                          <th className="text-right p-2">Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisData.accountSummary.map((acc, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-medium">{acc.account}</td>
                            <td className="p-2">{acc.name}</td>
                            <td className="text-right p-2">{formatCurrency(acc.beginningNAV, 'USD')}</td>
                            <td className="text-right p-2 font-medium">{formatCurrency(acc.endingNAV, 'USD')}</td>
                            <td className={`text-right p-2 font-medium ${acc.return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(acc.return)}
                            </td>
                            <td className="text-right p-2">{formatCurrency(acc.deposits, 'USD')}</td>
                            <td className="text-right p-2">{formatCurrency(acc.withdrawals, 'USD')}</td>
                            <td className="text-right p-2 text-green-600">{formatCurrency(acc.dividends, 'USD')}</td>
                            <td className="text-right p-2 text-green-600">{formatCurrency(acc.interest, 'USD')}</td>
                            <td className="text-right p-2 text-red-600">{formatCurrency(acc.fees, 'USD')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList className="grid w-full grid-cols-8">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="categories">Categories</TabsTrigger>
                <TabsTrigger value="positions">Positions</TabsTrigger>
                <TabsTrigger value="trades" className="flex items-center gap-1">
                  <Activity className="w-4 h-4" />
                  Trades
                </TabsTrigger>
                <TabsTrigger value="dividends" className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Dividends
                </TabsTrigger>
                <TabsTrigger value="cash" className="flex items-center gap-1">
                  <Percent className="w-4 h-4" />
                  Cash
                </TabsTrigger>
                <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
                <TabsTrigger value="ichimoku" className="flex items-center gap-1">
                  <Cloud className="w-4 h-4" />
                  Ichimoku
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Portfolio Allocation Pie Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Portfolio Allocation
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={analysisData.positionsByCategory.map(cat => ({
                              name: cat.category,
                              value: cat.totalValue
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {analysisData.positionsByCategory.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value as number, 'USD')} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Top Gainers & Losers */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        Top 10 Gainers & Losers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={[...analysisData.allPositions]
                            .sort((a, b) => Math.abs(b.unrealizedPL) - Math.abs(a.unrealizedPL))
                            .slice(0, 10)
                            .map(pos => ({
                              ticker: pos.ticker,
                              pl: pos.unrealizedPL,
                              plPercent: pos.unrealizedPLPercent
                            }))}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="ticker" type="category" width={80} />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'pl' ? formatCurrency(value as number) : formatPercent(value as number),
                              name === 'pl' ? 'P/L' : 'P/L %'
                            ]}
                          />
                          <Bar dataKey="pl" fill="#8884d8">
                            {[...analysisData.allPositions]
                              .sort((a, b) => Math.abs(b.unrealizedPL) - Math.abs(a.unrealizedPL))
                              .slice(0, 10)
                              .map((pos, index) => (
                                <Cell key={`cell-${index}`} fill={pos.unrealizedPL >= 0 ? '#22c55e' : '#ef4444'} />
                              ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="categories" className="space-y-4">
                {analysisData.positionsByCategory.map((category, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {category.category}
                          <Badge variant="secondary">{category.positions.length} positions</Badge>
                        </CardTitle>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{formatCurrency(category.totalValue, 'USD')}</p>
                          <p className={`text-sm font-medium ${category.totalUnrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(category.totalUnrealizedPL, 'USD')} ({formatPercent(category.totalUnrealizedPLPercent)})
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">Ticker</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-right p-2">Quantity</th>
                              <th className="text-right p-2">Market Value</th>
                              <th className="text-right p-2">Cost Basis</th>
                              <th className="text-right p-2">Unrealized P/L</th>
                              <th className="text-right p-2">P/L %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.positions.map((pos, posIdx) => (
                              <tr key={posIdx} className="border-b hover:bg-muted/50">
                                <td className="p-2 font-medium">{pos.ticker}</td>
                                <td className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">{pos.description}</td>
                                <td className="text-right p-2">{pos.quantity.toFixed(2)}</td>
                                <td className="text-right p-2">{formatCurrency(pos.marketValue, pos.currency)}</td>
                                <td className="text-right p-2">{formatCurrency(pos.costBasis, pos.currency)}</td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(pos.unrealizedPL, pos.currency)}
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPLPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(pos.unrealizedPLPercent)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="positions" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>All Positions ({analysisData.allPositions.length})</CardTitle>
                    <CardDescription>Click on any row to see detailed trading scorecard with profit targets and stop-loss levels</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Alert</th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('ticker')}
                            >
                              Ticker <SortIcon field="ticker" />
                            </th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('description')}
                            >
                              Description <SortIcon field="description" />
                            </th>
                            <th 
                              className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('sector')}
                            >
                              Sector <SortIcon field="sector" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('quantity')}
                            >
                              Quantity <SortIcon field="quantity" />
                            </th>
                            <th className="text-center p-2">Symbol</th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('avgCost')}
                            >
                              Avg Cost <SortIcon field="avgCost" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('currentPrice')}
                            >
                              Current Price <SortIcon field="currentPrice" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('marketValue')}
                            >
                              Market Value <SortIcon field="marketValue" />
                            </th>
                            <th className="text-right p-2">Next Target</th>
                            <th className="text-right p-2">Stop Loss (5%)</th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('unrealizedPL')}
                            >
                              Unrealized P/L <SortIcon field="unrealizedPL" />
                            </th>
                            <th 
                              className="text-right p-2 cursor-pointer hover:bg-muted/50 select-none"
                              onClick={() => handleSort('unrealizedPLPercent')}
                            >
                              P/L % <SortIcon field="unrealizedPLPercent" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getSortedPositions(analysisData.allPositions).map((pos, idx) => {
                            const gainPct = pos.unrealizedPLPercent;
                            const alertColor = gainPct >= 45 ? 'bg-red-500/10' :
                                             gainPct >= 30 ? 'bg-orange-500/10' :
                                             gainPct >= 15 ? 'bg-yellow-500/10' :
                                             gainPct < -5 ? 'bg-red-600/10' : '';
                            
                            const profitTargets = [
                              { ratio: '2:1', price: pos.avgCost * 1.10 },
                              { ratio: '3:1', price: pos.avgCost * 1.15 },
                              { ratio: '4:1', price: pos.avgCost * 1.20 },
                              { ratio: '6:1', price: pos.avgCost * 1.30 },
                            ];
                            const nextTarget = profitTargets.find(t => pos.currentPrice < t.price);
                            const stopLoss = pos.avgCost * 0.95;
                            
                            return (
                              <tr 
                                key={idx} 
                                className={cn(
                                  "border-b hover:bg-muted/80 cursor-pointer transition-all",
                                  alertColor
                                )}
                                onClick={() => {
                                  setSelectedPosition(pos);
                                  setScorecardOpen(true);
                                }}
                              >
                                <td className="p-2">
                                  <div className={cn(
                                    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap",
                                    gainPct >= 45 ? "bg-red-500 text-white animate-pulse" :
                                    gainPct >= 30 ? "bg-orange-500 text-white" :
                                    gainPct >= 15 ? "bg-yellow-500 text-white" :
                                    gainPct < -5 ? "bg-red-600 text-white" :
                                    "bg-gray-400 text-white"
                                  )}>
                                    {gainPct >= 45 ? '🎯 SELL' :
                                     gainPct >= 30 ? '⚡' :
                                     gainPct >= 15 ? '💰' :
                                     gainPct < -5 ? '🛑' : 'HOLD'}
                                  </div>
                                </td>
                                <td className="p-2 font-medium">{pos.ticker}</td>
                                <td className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">{pos.description}</td>
                                <td className="p-2 text-sm">{pos.sector}</td>
                                <td className="text-right p-2">{pos.quantity.toFixed(4)}</td>
                                <td className="text-center p-2 font-medium text-lg">{pos.currency}</td>
                                <td className="text-right p-2">{formatCurrency(pos.avgCost, pos.currency)}</td>
                                <td className="text-right p-2 font-bold">{formatCurrency(pos.currentPrice, pos.currency)}</td>
                                <td className="text-right p-2 font-medium">{formatCurrency(pos.marketValue, pos.currency)}</td>
                                <td className="text-right p-2">
                                  {nextTarget ? (
                                    <div>
                                      <div className="font-bold text-green-600 text-sm">{formatCurrency(nextTarget.price, pos.currency)}</div>
                                      <div className="text-xs text-muted-foreground">{nextTarget.ratio}</div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-green-600">Target reached ✓</span>
                                  )}
                                </td>
                                <td className="text-right p-2">
                                  <div className="font-bold text-red-600 text-sm">{formatCurrency(stopLoss, pos.currency)}</div>
                                  <div className="text-xs text-muted-foreground">-5%</div>
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(pos.unrealizedPL, pos.currency)}
                                </td>
                                <td className={`text-right p-2 font-medium ${pos.unrealizedPLPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(pos.unrealizedPLPercent)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Legend */}
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-3">
                        <div className="text-sm font-semibold">Alert Legend:</div>
                        <div className="flex items-center gap-6 text-sm flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="text-xs">≥45% - Take Profit Now</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-orange-500 rounded"></div>
                            <span className="text-xs">≥30% - Profit Zone</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                            <span className="text-xs">≥15% - Profit Alert</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-600 rounded"></div>
                            <span className="text-xs">&lt;-5% - Stop Loss Alert</span>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          <strong>Click any row</strong> to see detailed trading scorecard with profit targets and stop-loss recommendations
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="trades" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Activity className="w-5 h-5" />
                            Trades History ({filteredTrades.length}{filteredTrades.length !== (parseResult?.trades?.length || 0) ? ` of ${parseResult?.trades?.length || 0}` : ''})
                          </CardTitle>
                          <CardDescription className="mt-1.5">All executed trades from the Activity Statement with buy/sell details and realized P/L</CardDescription>
                        </div>
                      </div>
                      {parseResult?.trades && parseResult.trades.length > 0 && (
                        <DateRangeFilter
                          fromDate={tradesDateFrom}
                          toDate={tradesDateTo}
                          onFromChange={setTradesDateFrom}
                          onToChange={setTradesDateTo}
                        />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {filteredTrades.length > 0 ? (
                      <div className="space-y-4">
                        {/* Trade Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Total Trades</p>
                            <p className="text-2xl font-bold">{filteredTrades.length}</p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Buy Orders</p>
                            <p className="text-2xl font-bold text-green-600">
                              {filteredTrades.filter(t => t.action === 'BUY').length}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Sell Orders</p>
                            <p className="text-2xl font-bold text-red-600">
                              {filteredTrades.filter(t => t.action === 'SELL').length}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Total Realized P/L</p>
                            <p className={cn(
                              "text-2xl font-bold",
                              filteredTrades.reduce((sum, t) => sum + (t.realizedPL || 0), 0) >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            )}>
                              {formatCurrency(
                                filteredTrades.reduce((sum, t) => sum + (t.realizedPL || 0), 0),
                                parseResult?.currency || 'USD'
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Trades Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Action</th>
                                <th className="text-left p-2">Ticker</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-right p-2">Quantity</th>
                                <th className="text-right p-2">Price</th>
                                <th className="text-right p-2">Proceeds</th>
                                <th className="text-right p-2">Commission</th>
                                <th className="text-right p-2">Realized P/L</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTrades.map((trade, idx) => (
                                <tr key={idx} className="border-b hover:bg-muted/50">
                                  <td className="p-2 text-sm">{trade.date}</td>
                                  <td className="p-2">
                                    <Badge
                                      className={cn(
                                        "font-bold",
                                        trade.action === 'BUY'
                                          ? "bg-green-500 hover:bg-green-600"
                                          : "bg-red-500 hover:bg-red-600"
                                      )}
                                    >
                                      {trade.action}
                                    </Badge>
                                  </td>
                                  <td className="p-2 font-medium">{trade.ticker}</td>
                                  <td className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">
                                    {trade.description}
                                  </td>
                                  <td className="text-right p-2">{trade.quantity.toFixed(4)}</td>
                                  <td className="text-right p-2">{formatCurrency(trade.price, trade.currency)}</td>
                                  <td className="text-right p-2 font-medium">
                                    {formatCurrency(trade.proceeds, trade.currency)}
                                  </td>
                                  <td className="text-right p-2 text-red-600">
                                    -{formatCurrency(Math.abs(trade.commission), trade.currency)}
                                  </td>
                                  <td className={cn(
                                    "text-right p-2 font-bold",
                                    trade.realizedPL !== undefined && trade.realizedPL >= 0
                                      ? "text-green-600"
                                      : "text-red-600"
                                  )}>
                                    {trade.realizedPL !== undefined
                                      ? formatCurrency(trade.realizedPL, trade.currency)
                                      : '-'
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Total Summary Row */}
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                          <div className="flex flex-wrap justify-between items-center gap-4">
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Total Commissions Paid</p>
                              <p className="text-lg font-bold text-red-600">
                                -{formatCurrency(
                                  filteredTrades.reduce((sum, t) => sum + Math.abs(t.commission), 0),
                                  parseResult?.currency || 'USD'
                                )}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Total Proceeds</p>
                              <p className="text-lg font-bold">
                                {formatCurrency(
                                  filteredTrades.reduce((sum, t) => sum + t.proceeds, 0),
                                  parseResult?.currency || 'USD'
                                )}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Net Realized P/L</p>
                              <p className={cn(
                                "text-xl font-bold",
                                filteredTrades.reduce((sum, t) => sum + (t.realizedPL || 0), 0) >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              )}>
                                {formatCurrency(
                                  filteredTrades.reduce((sum, t) => sum + (t.realizedPL || 0), 0),
                                  parseResult?.currency || 'USD'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">
                          {parseResult?.trades && parseResult.trades.length > 0 
                            ? "No trades match the selected date range" 
                            : "No trades found in this statement"}
                        </p>
                        <p className="text-sm mt-2">
                          {parseResult?.trades && parseResult.trades.length > 0 
                            ? "Try adjusting the date filter or click Clear to see all trades."
                            : "Trade history is available in Activity Statements that contain executed orders."}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="dividends" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5" />
                            Dividends & Withholding Tax ({filteredDividends.length}{filteredDividends.length !== (parseResult?.dividends?.length || 0) ? ` of ${parseResult?.dividends?.length || 0}` : ''})
                          </CardTitle>
                          <CardDescription className="mt-1.5">All dividend payments and withholding taxes from the Activity Statement</CardDescription>
                        </div>
                        {parseResult?.dividends && parseResult.dividends.length > 0 && (
                          <Button onClick={exportDividendsToCSV} variant="outline" className="flex items-center gap-2">
                            <Download className="w-4 h-4" />
                            Export for Tax
                          </Button>
                        )}
                      </div>
                      {parseResult?.dividends && parseResult.dividends.length > 0 && (
                        <DateRangeFilter
                          fromDate={dividendsDateFrom}
                          toDate={dividendsDateTo}
                          onFromChange={setDividendsDateFrom}
                          onToChange={setDividendsDateTo}
                        />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {filteredDividends.length > 0 ? (
                      <div className="space-y-4">
                        {/* Dividend Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Total Entries</p>
                            <p className="text-2xl font-bold">{filteredDividends.length}</p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Gross Dividends</p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(
                                filteredDividends
                                  .filter(d => d.amount > 0 && !d.description.toLowerCase().includes('tax'))
                                  .reduce((sum, d) => sum + d.amount, 0),
                                parseResult?.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Withholding Tax</p>
                            <p className="text-2xl font-bold text-red-600">
                              {formatCurrency(
                                Math.abs(filteredDividends
                                  .filter(d => d.amount < 0 || d.description.toLowerCase().includes('tax'))
                                  .reduce((sum, d) => sum + d.amount, 0)),
                                parseResult?.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Net Dividends</p>
                            <p className={cn(
                              "text-2xl font-bold",
                              filteredDividends.reduce((sum, d) => sum + d.amount, 0) >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            )}>
                              {formatCurrency(
                                filteredDividends.reduce((sum, d) => sum + d.amount, 0),
                                parseResult?.currency || 'USD'
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Dividends by Ticker Summary */}
                        {(() => {
                          const dividendsByTicker = filteredDividends.reduce((acc, div) => {
                            if (!div.ticker) return acc;
                            if (!acc[div.ticker]) {
                              acc[div.ticker] = { gross: 0, tax: 0, net: 0 };
                            }
                            if (div.description.toLowerCase().includes('tax') || div.amount < 0) {
                              acc[div.ticker].tax += Math.abs(div.amount);
                            } else {
                              acc[div.ticker].gross += div.amount;
                            }
                            acc[div.ticker].net = acc[div.ticker].gross - acc[div.ticker].tax;
                            return acc;
                          }, {} as Record<string, { gross: number; tax: number; net: number }>);

                          const tickerEntries = Object.entries(dividendsByTicker).sort((a, b) => b[1].net - a[1].net);

                          return tickerEntries.length > 0 && (
                            <Card className="mb-4">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-base">Dividends by Stock</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b">
                                        <th className="text-left p-2">Ticker</th>
                                        <th className="text-right p-2">Gross Dividend</th>
                                        <th className="text-right p-2">Withholding Tax</th>
                                        <th className="text-right p-2">Net Dividend</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tickerEntries.map(([ticker, data]) => (
                                        <tr key={ticker} className="border-b hover:bg-muted/50">
                                          <td className="p-2 font-medium">{ticker}</td>
                                          <td className="text-right p-2 text-green-600">
                                            {formatCurrency(data.gross, parseResult?.currency || 'USD')}
                                          </td>
                                          <td className="text-right p-2 text-red-600">
                                            -{formatCurrency(data.tax, parseResult?.currency || 'USD')}
                                          </td>
                                          <td className={cn(
                                            "text-right p-2 font-bold",
                                            data.net >= 0 ? "text-green-600" : "text-red-600"
                                          )}>
                                            {formatCurrency(data.net, parseResult?.currency || 'USD')}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })()}

                        {/* Full Dividends Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Type</th>
                                <th className="text-left p-2">Ticker</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-right p-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDividends.map((div, idx) => {
                                const isTax = div.description.toLowerCase().includes('tax') || div.amount < 0;
                                return (
                                  <tr key={idx} className="border-b hover:bg-muted/50">
                                    <td className="p-2 text-sm">{div.date}</td>
                                    <td className="p-2">
                                      <Badge
                                        className={cn(
                                          "font-bold",
                                          isTax
                                            ? "bg-red-500 hover:bg-red-600"
                                            : "bg-green-500 hover:bg-green-600"
                                        )}
                                      >
                                        {isTax ? 'TAX' : 'DIV'}
                                      </Badge>
                                    </td>
                                    <td className="p-2 font-medium">{div.ticker || '-'}</td>
                                    <td className="p-2 text-sm text-muted-foreground max-w-[300px] truncate">
                                      {div.description}
                                    </td>
                                    <td className={cn(
                                      "text-right p-2 font-bold",
                                      div.amount >= 0 ? "text-green-600" : "text-red-600"
                                    )}>
                                      {formatCurrency(div.amount, div.currency)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Total Summary */}
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                          <div className="flex flex-wrap justify-between items-center gap-4">
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Gross Dividends Received</p>
                              <p className="text-lg font-bold text-green-600">
                                {formatCurrency(
                                  parseResult.dividends
                                    .filter(d => d.amount > 0 && !d.description.toLowerCase().includes('tax'))
                                    .reduce((sum, d) => sum + d.amount, 0),
                                  parseResult.currency || 'USD'
                                )}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Total Withholding Tax</p>
                              <p className="text-lg font-bold text-red-600">
                                -{formatCurrency(
                                  Math.abs(parseResult.dividends
                                    .filter(d => d.amount < 0 || d.description.toLowerCase().includes('tax'))
                                    .reduce((sum, d) => sum + d.amount, 0)),
                                  parseResult.currency || 'USD'
                                )}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm text-muted-foreground">Net Dividends</p>
                              <p className={cn(
                                "text-xl font-bold",
                                parseResult.dividends.reduce((sum, d) => sum + d.amount, 0) >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              )}>
                                {formatCurrency(
                                  parseResult.dividends.reduce((sum, d) => sum + d.amount, 0),
                                  parseResult.currency || 'USD'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No dividends found in this statement</p>
                        <p className="text-sm mt-2">
                          Dividend information is available in Activity Statements that include dividend payments.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="cash" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="w-5 h-5" />
                      Cash Transactions ({parseResult?.cashTransactions?.length || 0})
                    </CardTitle>
                    <CardDescription>Deposits, withdrawals, interest, and fees from the Activity Statement</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {parseResult?.cashTransactions && parseResult.cashTransactions.length > 0 ? (
                      <div className="space-y-4">
                        {/* Total Fees Dashboard Card */}
                        <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-red-500" />
                              Total Fees Summary
                            </CardTitle>
                            <CardDescription>All fees and commissions across trades and cash transactions</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="p-4 bg-background/50 rounded-lg border">
                                <p className="text-sm text-muted-foreground">Trading Commissions</p>
                                <p className="text-xl font-bold text-red-600">
                                  -{formatCurrency(
                                    (parseResult?.trades || []).reduce((sum, t) => sum + Math.abs(t.commission), 0),
                                    parseResult?.currency || 'USD'
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  From {parseResult?.trades?.length || 0} trades
                                </p>
                              </div>
                              <div className="p-4 bg-background/50 rounded-lg border">
                                <p className="text-sm text-muted-foreground">Account Fees</p>
                                <p className="text-xl font-bold text-red-600">
                                  -{formatCurrency(
                                    Math.abs(parseResult.cashTransactions
                                      .filter(t => t.type === 'fee')
                                      .reduce((sum, t) => sum + t.amount, 0)),
                                    parseResult?.currency || 'USD'
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  From {parseResult.cashTransactions.filter(t => t.type === 'fee').length} fee entries
                                </p>
                              </div>
                              <div className="p-4 bg-background/50 rounded-lg border">
                                <p className="text-sm text-muted-foreground">Withholding Tax</p>
                                <p className="text-xl font-bold text-red-600">
                                  -{formatCurrency(
                                    Math.abs((parseResult?.dividends || [])
                                      .filter(d => d.amount < 0 || d.description.toLowerCase().includes('tax'))
                                      .reduce((sum, d) => sum + d.amount, 0)),
                                    parseResult?.currency || 'USD'
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  From dividends
                                </p>
                              </div>
                              <div className="p-4 bg-red-500/20 rounded-lg border-2 border-red-500">
                                <p className="text-sm font-medium text-red-600">Total Fees & Taxes</p>
                                <p className="text-2xl font-bold text-red-600">
                                  -{formatCurrency(
                                    (parseResult?.trades || []).reduce((sum, t) => sum + Math.abs(t.commission), 0) +
                                    Math.abs(parseResult.cashTransactions.filter(t => t.type === 'fee').reduce((sum, t) => sum + t.amount, 0)) +
                                    Math.abs((parseResult?.dividends || []).filter(d => d.amount < 0 || d.description.toLowerCase().includes('tax')).reduce((sum, d) => sum + d.amount, 0)),
                                    parseResult?.currency || 'USD'
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  All deductions combined
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Cash Transaction Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Deposits</p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(
                                parseResult.cashTransactions
                                  .filter(t => t.type === 'deposit')
                                  .reduce((sum, t) => sum + t.amount, 0),
                                parseResult.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Withdrawals</p>
                            <p className="text-2xl font-bold text-red-600">
                              {formatCurrency(
                                Math.abs(parseResult.cashTransactions
                                  .filter(t => t.type === 'withdrawal')
                                  .reduce((sum, t) => sum + t.amount, 0)),
                                parseResult.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Interest Earned</p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(
                                parseResult.cashTransactions
                                  .filter(t => t.type === 'interest')
                                  .reduce((sum, t) => sum + t.amount, 0),
                                parseResult.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Fees Paid</p>
                            <p className="text-2xl font-bold text-red-600">
                              {formatCurrency(
                                Math.abs(parseResult.cashTransactions
                                  .filter(t => t.type === 'fee')
                                  .reduce((sum, t) => sum + t.amount, 0)),
                                parseResult.currency || 'USD'
                              )}
                            </p>
                          </div>
                          <div className="p-4 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">Net Cash Flow</p>
                            <p className={cn(
                              "text-2xl font-bold",
                              parseResult.cashTransactions.reduce((sum, t) => sum + t.amount, 0) >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            )}>
                              {formatCurrency(
                                parseResult.cashTransactions.reduce((sum, t) => sum + t.amount, 0),
                                parseResult.currency || 'USD'
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Cash Balances by Currency */}
                        {parseResult.cashBalances && parseResult.cashBalances.length > 0 && (
                          <Card className="mb-4">
                            <CardHeader className="pb-3">
                              <CardTitle className="text-base">Cash Balances by Currency</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {parseResult.cashBalances.map((balance, idx) => (
                                  <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                                    <p className="text-sm text-muted-foreground">{balance.currency}</p>
                                    <p className={cn(
                                      "text-xl font-bold",
                                      balance.balance >= 0 ? "text-foreground" : "text-red-600"
                                    )}>
                                      {formatCurrency(balance.balance, balance.currency)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Cash Transactions Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2">Date</th>
                                <th className="text-left p-2">Type</th>
                                <th className="text-left p-2">Description</th>
                                <th className="text-center p-2">Currency</th>
                                <th className="text-right p-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parseResult.cashTransactions.map((txn, idx) => {
                                const typeColors: Record<string, string> = {
                                  deposit: "bg-green-500 hover:bg-green-600",
                                  withdrawal: "bg-red-500 hover:bg-red-600",
                                  dividend: "bg-blue-500 hover:bg-blue-600",
                                  interest: "bg-emerald-500 hover:bg-emerald-600",
                                  fee: "bg-orange-500 hover:bg-orange-600",
                                  other: "bg-gray-500 hover:bg-gray-600",
                                };
                                const typeLabels: Record<string, string> = {
                                  deposit: "DEPOSIT",
                                  withdrawal: "WITHDRAW",
                                  dividend: "DIVIDEND",
                                  interest: "INTEREST",
                                  fee: "FEE",
                                  other: "OTHER",
                                };
                                return (
                                  <tr key={idx} className="border-b hover:bg-muted/50">
                                    <td className="p-2 text-sm">{txn.date}</td>
                                    <td className="p-2">
                                      <Badge className={cn("font-bold", typeColors[txn.type] || typeColors.other)}>
                                        {typeLabels[txn.type] || txn.type.toUpperCase()}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-sm text-muted-foreground max-w-[300px] truncate">
                                      {txn.description}
                                    </td>
                                    <td className="text-center p-2 font-medium">{txn.currency}</td>
                                    <td className={cn(
                                      "text-right p-2 font-bold",
                                      txn.amount >= 0 ? "text-green-600" : "text-red-600"
                                    )}>
                                      {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount, txn.currency)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Summary by Type */}
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                          <h4 className="font-semibold mb-3">Summary by Transaction Type</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {(() => {
                              const byType = parseResult.cashTransactions.reduce((acc, txn) => {
                                if (!acc[txn.type]) {
                                  acc[txn.type] = { count: 0, total: 0 };
                                }
                                acc[txn.type].count++;
                                acc[txn.type].total += txn.amount;
                                return acc;
                              }, {} as Record<string, { count: number; total: number }>);

                              return Object.entries(byType).map(([type, data]) => (
                                <div key={type} className="space-y-1">
                                  <p className="text-sm text-muted-foreground capitalize">{type}s</p>
                                  <p className={cn(
                                    "text-lg font-bold",
                                    data.total >= 0 ? "text-green-600" : "text-red-600"
                                  )}>
                                    {formatCurrency(data.total, parseResult.currency || 'USD')}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{data.count} transactions</p>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Percent className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No cash transactions found in this statement</p>
                        <p className="text-sm mt-2">
                          Cash transaction details are available in Activity Statements that include deposits, withdrawals, interest, or fees.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="benchmarks" className="space-y-4">
                {/* Benchmark Summary Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Summary</CardTitle>
                    <CardDescription>Comparison across different time periods</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Index</th>
                            <th className="text-right p-2">MTD</th>
                            <th className="text-right p-2">QTD</th>
                            <th className="text-right p-2">YTD</th>
                            <th className="text-right p-2">1 Year</th>
                            <th className="text-right p-2">Since Inception</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysisData.benchmarkSummary.map((benchmark, idx) => (
                            <tr key={idx} className={`border-b hover:bg-muted/50 ${benchmark.name === 'Portfolio' ? 'bg-primary/5 font-medium' : ''}`}>
                              <td className="p-2">{benchmark.name}</td>
                              <td className={`text-right p-2 ${benchmark.mtd !== null && benchmark.mtd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.mtd !== null ? formatPercent(benchmark.mtd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.qtd !== null && benchmark.qtd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.qtd !== null ? formatPercent(benchmark.qtd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.ytd !== null && benchmark.ytd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.ytd !== null ? formatPercent(benchmark.ytd) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.oneYear !== null && benchmark.oneYear >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.oneYear !== null ? formatPercent(benchmark.oneYear) : '-'}
                              </td>
                              <td className={`text-right p-2 ${benchmark.sinceInception !== null && benchmark.sinceInception >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {benchmark.sinceInception !== null ? formatPercent(benchmark.sinceInception) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Performance Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Performance Comparison</CardTitle>
                    <CardDescription>Portfolio vs major indices (monthly returns)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={analysisData.monthlyComparison.filter(d => d.portfolio !== null)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis label={{ value: 'Return %', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => value !== null ? `${(value as number).toFixed(2)}%` : '-'} />
                        <Legend />
                        <Line type="monotone" dataKey="portfolio" stroke="#8884d8" strokeWidth={3} name="Your Portfolio" connectNulls />
                        <Line type="monotone" dataKey="sp500" stroke="#82ca9d" strokeWidth={2} name="S&P 500 (SPXTR)" connectNulls />
                        <Line type="monotone" dataKey="efa" stroke="#ffc658" strokeWidth={2} name="EFA" connectNulls />
                        <Line type="monotone" dataKey="vt" stroke="#ff7c7c" strokeWidth={2} name="VT" connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Quarterly Performance Chart */}
                {analysisData.quarterlyComparison.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Quarterly Performance Comparison</CardTitle>
                      <CardDescription>Portfolio vs major indices (quarterly returns)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analysisData.quarterlyComparison.filter(d => d.portfolio !== null)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" />
                          <YAxis label={{ value: 'Return %', angle: -90, position: 'insideLeft' }} />
                          <Tooltip formatter={(value) => value !== null ? `${(value as number).toFixed(2)}%` : '-'} />
                          <Legend />
                          <Bar dataKey="portfolio" fill="#8884d8" name="Your Portfolio" />
                          <Bar dataKey="sp500" fill="#82ca9d" name="S&P 500 (SPXTR)" />
                          <Bar dataKey="efa" fill="#ffc658" name="EFA" />
                          <Bar dataKey="vt" fill="#ff7c7c" name="VT" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="ichimoku" className="space-y-4">
                <IchimokuAlerts tickers={analysisData.allPositions.map(p => p.ticker)} />
              </TabsContent>
            </Tabs>

            {/* Stock Scorecards - Action Summary */}
            <div className="mt-12 space-y-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Target className="h-6 w-6 text-primary" />
                Action Scorecards - Quick Decision Guide
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Take Profit Now */}
                {(() => {
                  const takeProfitPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 45);
                  return takeProfitPositions.length > 0 && (
                    <Card className="border-2 border-red-500 bg-red-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse"></div>
                          Take Profit Now ({takeProfitPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {takeProfitPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-red-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Sell 50%:</span>
                                <span className="font-semibold">{Math.floor(pos.quantity * 0.5)} shares</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Lock in:</span>
                                <span className="font-semibold text-green-600">
                                  {formatCurrency(Math.floor(pos.quantity * 0.5) * pos.currentPrice, pos.currency)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-red-600">🎯 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Profit Zone */}
                {(() => {
                  const profitZonePositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 30 && p.unrealizedPLPercent < 45);
                  return profitZonePositions.length > 0 && (
                    <Card className="border-2 border-orange-500 bg-orange-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-orange-500" />
                          Profit Zone ({profitZonePositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {profitZonePositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-orange-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Current:</span>
                                <span className="font-semibold">{formatCurrency(pos.currentPrice, pos.currency)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Stop Loss:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(pos.avgCost * 0.95, pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-orange-600">⚡ Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Profit Alert */}
                {(() => {
                  const profitAlertPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 15 && p.unrealizedPLPercent < 30);
                  return profitAlertPositions.length > 0 && (
                    <Card className="border-2 border-yellow-500 bg-yellow-500/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-yellow-600" />
                          Profit Alert ({profitAlertPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {profitAlertPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge className="bg-yellow-500 text-white">+{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Target:</span>
                                <span className="font-semibold text-green-600">{formatCurrency(pos.avgCost * 1.15, pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-yellow-700">💰 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Stop Loss Alert */}
                {(() => {
                  const stopLossPositions = analysisData.allPositions.filter(p => p.unrealizedPLPercent < -5);
                  return stopLossPositions.length > 0 && (
                    <Card className="border-2 border-red-600 bg-red-600/5">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ShieldAlert className="h-5 w-5 text-red-600" />
                          Stop Loss Alert ({stopLossPositions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {stopLossPositions.map((pos) => (
                          <div 
                            key={pos.ticker} 
                            className="p-3 bg-background rounded-lg border border-red-300 cursor-pointer hover:bg-red-50 transition-colors"
                            onClick={() => {
                              setSelectedPosition(pos);
                              setScorecardOpen(true);
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold">{pos.ticker}</span>
                              <Badge variant="destructive">{pos.unrealizedPLPercent.toFixed(2)}%</Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Loss:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(Math.abs(pos.unrealizedPL), pos.currency)}</span>
                              </div>
                            </div>
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-xs font-medium text-red-600">🛑 Click for full scorecard</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>

              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Portfolio Alert Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-red-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-red-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 45).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Take Profit Now</div>
                    </div>
                    <div className="text-center p-4 bg-orange-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-orange-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 30 && p.unrealizedPLPercent < 45).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">In Profit Zone</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-500/10 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-600">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent >= 15 && p.unrealizedPLPercent < 30).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Profit Alerts</div>
                    </div>
                    <div className="text-center p-4 bg-red-600/10 rounded-lg">
                      <div className="text-3xl font-bold text-red-700">
                        {analysisData.allPositions.filter(p => p.unrealizedPLPercent < -5).length}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">Stop Loss Alerts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Individual Stock Scorecard Dialog */}
        <IndividualStockScorecard 
          position={selectedPosition}
          open={scorecardOpen}
          onClose={() => setScorecardOpen(false)}
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              How to Export CSV from IBKR
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-medium">Step 1: Access Reports</h3>
              <p className="text-sm text-muted-foreground">
                Log in to your IBKR account → Performance & Reports → Statements
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Step 2: Select Period & Format</h3>
              <p className="text-sm text-muted-foreground">
                Choose "Custom" or "Year to Date", select date range, and pick "CSV" format
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Step 3: Download & Upload</h3>
              <p className="text-sm text-muted-foreground">
                Download the CSV file and upload it here for comprehensive analysis
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default IBKRImport;
